<script setup>
import { ref, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import Button from "@/shared/ui/button/Button.vue";

const router = useRouter();

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  participantUuid: {
    type: String,
    required: true,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  isSuccessSign: {
    type: Boolean,
    default: false,
  },
  signError: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["close", "sign"]);

const signMethod = ref("hand");
const signStep = ref("method");
const isDrawing = ref(false);
const hasSignature = ref(false);
const signatureDataUrl = ref("");
const signCanvasRef = ref(null);

watch(
  () => props.visible,
  (val) => {
    if (!val) return;
    resetFlow();
  }
);

watch(
  () => props.isSubmitting,
  (val) => {
    if (val === true) {
      signStep.value = "loading";
    }
  }
);

watch(
  () => props.isSuccessSign,
  (val) => {
    if (val === true) {
      signStep.value = "success";
    }
  }
);

watch(
  () => props.signError,
  (val) => {
    if (val) {
      signStep.value = "loading";
    }
  }
);

function resetFlow() {
  signMethod.value = "hand";
  isDrawing.value = false;
  hasSignature.value = false;
  signatureDataUrl.value = "";
  signStep.value = "draw";
  nextTick(() => prepareCanvas());
}

function close() {
  emit("close");
}

function startDrawing(event) {
  if (signStep.value !== "draw") return;
  isDrawing.value = true;
  const { x, y } = getCanvasPoint(event);
  const ctx = signCanvasRef.value?.getContext("2d");
  if (!ctx) return;
  ctx.beginPath();
  ctx.moveTo(x, y);
}

function draw(event) {
  if (!isDrawing.value || signStep.value !== "draw") return;
  const { x, y } = getCanvasPoint(event);
  const ctx = signCanvasRef.value?.getContext("2d");
  if (!ctx) return;
  ctx.lineTo(x, y);
  ctx.stroke();
  hasSignature.value = true;
}

function endDrawing() {
  if (!isDrawing.value) return;
  isDrawing.value = false;
}

function getCanvasPoint(event) {
  const canvas = signCanvasRef.value;
  const rect = canvas.getBoundingClientRect();
  const clientX = event.touches ? event.touches[0].clientX : event.clientX;
  const clientY = event.touches ? event.touches[0].clientY : event.clientY;
  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  };
}

function prepareCanvas() {
  const canvas = signCanvasRef.value;
  if (!canvas) return;
  const ratio = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * ratio;
  canvas.height = rect.height * ratio;
  const ctx = canvas.getContext("2d");
  ctx.scale(ratio, ratio);
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.strokeStyle = getComputedStyle(document.documentElement)
    .getPropertyValue("--foreground")
    .trim();
  ctx.strokeStyle = `hsl(${ctx.strokeStyle})`;
}

function clearSignature() {
  const canvas = signCanvasRef.value;
  const ctx = canvas?.getContext("2d");
  if (!canvas || !ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  hasSignature.value = false;
  signatureDataUrl.value = "";
}

function nextFromDraw() {
  if (!hasSignature.value) return;
  const canvas = signCanvasRef.value;
  signatureDataUrl.value = canvas.toDataURL("image/png");
  signStep.value = "preview";
}

function backToDraw() {
  signStep.value = "draw";
  nextTick(() => prepareCanvas());
}

function handleSign() {
  if (props.isSubmitting || !hasSignature.value) return;
  emit("sign", signatureDataUrl.value, "BASE64");
}

function goToHome() {
  router.push({ name: "client" });
  close();
}

function retry() {
  signStep.value = "preview";
}
</script>

<template>
  <div v-if="visible" class="sign-flow">
    <div class="sign-header">
      <Button variant="outline" size="icon" @click="close">
        <svg-icon name="huge-chevron-left" class="w-5 h-5" />
      </Button>
      <div class="title">{{ $t("Подписание документа") }}</div>
      <div class="placeholder"></div>
    </div>

    <div class="sign-body">
      <template v-if="signStep === 'draw'">
        <div
          class="text-start justify-start text-muted-foreground text-sm font-normal"
        >
          {{
            $t(
              "Поверните телефон в горизонтальное положение и распишитесь от руки в указанной области "
            )
          }}
        </div>
        <div class="sign-canvas">
          <canvas
            ref="signCanvasRef"
            class="canvas"
            @mousedown="startDrawing"
            @mousemove="draw"
            @mouseup="endDrawing"
            @mouseleave="endDrawing"
            @touchstart.prevent="startDrawing"
            @touchmove.prevent="draw"
            @touchend="endDrawing"
          ></canvas>
        </div>
        <div class="sign-actions">
          <Button @click="clearSignature" variant="outline" class="w-1/2">
            {{ $t("Сбросить") }}
          </Button>
          <Button :disabled="!hasSignature" @click="nextFromDraw" class="w-1/2">
            {{ $t("Далее") }}
          </Button>
        </div>
      </template>

      <template v-else-if="signStep === 'preview'">
        <div class="sign-preview">
          <img v-if="signatureDataUrl" :src="signatureDataUrl" alt="sign" />
        </div>
        <div class="sign-actions">
          <Button variant="outline" @click="backToDraw" class="w-1/2">
            {{ $t("Сбросить") }}
          </Button>
          <Button @click="handleSign" class="w-1/2">
            {{ $t("Подписать") }}
          </Button>
        </div>
      </template>

      <template v-else-if="signStep === 'loading'">
        <div class="sign-loading">
          <div v-if="isSubmitting" class="flex flex-col items-center gap-4">
            <div class="loader"></div>
            <div class="flex flex-col gap-3">
              <div
                class="text-center justify-start text-foreground text-2xl font-bold"
              >
                {{ $t("Подписываем документ") }}
              </div>
              <div class="loading-text">{{ $t("Пожалуйста подождите") }}</div>
            </div>
          </div>

          <div v-else-if="signError" class="flex flex-col items-center gap-6">
            <div class="flex flex-col gap-2">
              <div class="error-text text-red-500 text-center">
                {{ signError }}
              </div>
            </div>
            <Button variant="default" @click="retry">
              {{ $t("Попробовать снова") }}
            </Button>
          </div>
        </div>
      </template>

      <template v-else-if="signStep === 'success'">
        <div class="sign-success">
          <div class="success-icon">🎉</div>
          <div class="flex flex-col gap-3">
            <div
              class="text-center justify-start text-foreground text-xl font-bold"
            >
              {{ $t("Документ подписан") }}
            </div>
            <div class="success-title">
              {{
                $t(
                  "Вы можете использовать документ по ссылке, либо скачать подписанный файл к себе на устройство"
                )
              }}
            </div>
          </div>

          <Button variant="default" class="action-button" @click="goToHome">
            {{ $t("Вернуться на главную") }}
          </Button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.sign-flow {
  @apply fixed inset-0 z-50 flex flex-col;
  background-color: hsl(var(--background));
}

.sign-header {
  @apply w-full flex items-center justify-between px-4 py-3;
  border-bottom: 1px solid hsl(var(--border));
}

.title {
  @apply text-foreground text-base font-bold;
}

.placeholder {
  @apply w-10 h-10 opacity-0;
}

.sign-body {
  @apply flex-1 flex flex-col gap-4 px-4 py-6;
}

.subtitle {
  @apply text-foreground text-xl font-bold;
}

.sign-options {
  @apply h-full flex flex-col gap-3;
}

.sign-option {
  @apply w-full p-4 rounded-2xl text-left;
  @apply flex flex-col  gap-3;
  border: 1px solid hsl(var(--border));
  background-color: hsl(var(--background));
  transition: all 0.15s ease;

  &:hover {
    background-color: hsl(var(--muted));
  }

  &.active {
    border-color: hsl(var(--foreground));
    background-color: hsl(var(--muted));
  }
}

.option-title {
  @apply text-sm font-semibold text-foreground;
}

.option-subtitle {
  @apply text-xs text-muted-foreground;
}

.option-badge {
  @apply text-xs text-muted-foreground px-1.5 py-0.5 rounded-md;
  background-color: hsl(var(--muted));
}

.sign-canvas {
  @apply w-full rounded-2xl;
  border: 1px solid hsl(var(--border));
  background-color: hsl(var(--background));
  height: calc(100vh - 230px);
}

.canvas {
  @apply w-full h-full rounded-2xl;
}

.sign-actions {
  @apply fixed left-1/2 -translate-x-1/2 bottom-4;
  @apply w-full max-w-[560px] px-4;
  @apply flex items-center gap-3;
  z-index: 10;
}

.action-button {
  @apply w-full rounded-full;
}

.sign-preview {
  @apply w-full rounded-2xl flex items-center justify-center;
  border: 1px solid hsl(var(--border));
  background-color: hsl(var(--background));
  height: calc(100vh - 190px);
}

.sign-preview img {
  @apply max-h-full;
}

.sign-loading {
  @apply flex-1 flex flex-col items-center justify-center gap-3;
}

.loader {
  @apply w-10 h-10 rounded-full;
  border: 2px solid hsl(var(--border));
  border-top-color: hsl(var(--foreground));
  animation: spin 1s linear infinite;
}

.loading-text {
  @apply text-sm text-muted-foreground text-center;
}

.sign-success {
  @apply flex-1 flex flex-col items-center justify-center gap-6 mx-4;
}

.success-icon {
  @apply text-5xl;
}

.success-title {
  @apply text-sm text-foreground text-center;
}

.error-icon,
.error-text {
  @apply text-center;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
